import type { GameState, SkillState } from './types.ts';
import {
  doctrineOfflineEfficiencyBonus, doctrineOfflineHours, doctrineProductionMultiplier, doctrineXpMultiplier
} from './doctrines.ts';
import type { SkillId } from './catalog.ts';
import {
  artifactManualMultiplier, artifactOfflineEfficiencyBonus, artifactProductionMultiplier, artifactXpMultiplier
} from './artifacts.ts';
import {
  constellationDeepXpMultiplier, constellationGlobalMultiplier, constellationTwinEchoMultiplier
} from './constellation.ts';

export const MAX_SKILL_LEVEL = 200;
export const MAX_TOOL_TIER = 40;
export const MAX_BUILDING_LEVEL = 40;
export const DEEP_SKILL_LEVEL = 120;
export const BASE_XP_PER_SECOND = 5;
export const BASE_RESOURCE_PER_SECOND = 0.15;
export const BASE_OFFLINE_HOURS = 12;
export const MAX_OFFLINE_HOURS = 24;

export function xpForLevel(level: number): number {
  if (level <= 1) return 0;
  let points = 0;
  for (let current = 1; current < level; current += 1) {
    points += Math.floor(current + 300 * 2 ** (current / 7));
  }
  return Math.floor(points / 4);
}

export const MAX_SKILL_XP = xpForLevel(MAX_SKILL_LEVEL + 1) - 1;

export function levelFromXp(xp: number): number {
  if (xp >= MAX_SKILL_XP) return MAX_SKILL_LEVEL;
  let low = 1;
  let high = MAX_SKILL_LEVEL;
  while (low < high) {
    const middle = Math.floor((low + high + 1) / 2);
    if (xpForLevel(middle) <= xp) low = middle;
    else high = middle - 1;
  }
  return low;
}

export function exponentialCost(base: number, growth: number, owned: number): number {
  return Math.max(1, Math.ceil(base * growth ** Math.max(0, owned)));
}

export function bulkCost(base: number, growth: number, owned: number, quantity: number): number {
  const count = Math.max(0, Math.floor(quantity));
  if (count === 0) return 0;
  return Math.ceil(base * growth ** Math.max(0, owned) * ((growth ** count - 1) / (growth - 1)));
}

export function globalProductionMultiplier(state: GameState, skill: SkillId = state.run.activeSkill, mode: 'active' | 'offline' = 'active'): number {
  return 1.12 ** (state.prestige.upgrades.eternalCraft ?? 0) * harmonicBondMultiplier(state) * doctrineProductionMultiplier(state, skill, mode) * artifactProductionMultiplier(state, skill) * constellationGlobalMultiplier(state);
}

export function globalXpMultiplier(state: GameState, skill: SkillId = state.run.activeSkill, mode: 'active' | 'offline' = 'active'): number {
  return 1.1 ** (state.prestige.upgrades.echoingKnowledge ?? 0) * harmonicBondMultiplier(state) * doctrineXpMultiplier(state, skill, mode) * artifactXpMultiplier(state, skill) * constellationGlobalMultiplier(state);
}

export function harmonicBondMultiplier(state: GameState): number {
  const rank = state.prestige.upgrades.harmonicBond ?? 0;
  const earnedSkillLevels = Object.values(state.run.skills).reduce((total, skill) => total + Math.max(0, skill.level - 1), 0);
  return 1 + Math.min(1, earnedSkillLevels * rank * 0.00004);
}

export function manualActionMultiplier(state: GameState): number {
  return (1 + (state.prestige.upgrades.swiftHands ?? 0) * 0.25) * artifactManualMultiplier(state);
}

export function twinEchoEfficiency(state: GameState): number {
  const rank = state.prestige.upgrades.twinEchoes ?? 0;
  return ([0, 0.12, 0.18, 0.25][Math.min(3, Math.max(0, rank))] ?? 0) * constellationTwinEchoMultiplier(state);
}

export function beyondTheVeilMultiplier(state: GameState): number {
  return 2 ** Math.min(8, Math.max(0, state.prestige.upgrades.beyondTheVeil ?? 0)) * constellationDeepXpMultiplier(state);
}

export function skillMultiplier(skill: SkillState): number {
  const toolMultiplier = 1 + Math.min(MAX_TOOL_TIER, Math.max(0, skill.toolTier)) * 0.5;
  const buildingMultiplier = 1.05 ** Math.min(MAX_BUILDING_LEVEL, Math.max(0, skill.buildingLevel));
  return toolMultiplier * buildingMultiplier;
}

export function offlineCapSeconds(state: GameState): number {
  const hours = Math.min(MAX_OFFLINE_HOURS, BASE_OFFLINE_HOURS + (state.prestige.upgrades.deepSlumber ?? 0) * 2 + doctrineOfflineHours(state));
  return hours * 60 * 60;
}

export function offlineEfficiency(state: GameState): number {
  return Math.min(1, 0.75 + (state.prestige.upgrades.perfectRecall ?? 0) * 0.05 + doctrineOfflineEfficiencyBonus(state) + artifactOfflineEfficiencyBonus(state));
}
